import { useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Activity, CalendarClock, RefreshCw, Share2, Sparkles, TrendingUp } from "lucide-react";

import api, { restoreAuthFromStorage } from "../api/Client";
import styles from "./StatisticsPage.module.css";

type HumanScoreBreakdown = {
  attendance?: { weeksAttended?: number; windowWeeks?: number; score?: number };
  frequency?: { trainingsLast12Weeks?: number; avgPerWeek?: number; score?: number };
  extraServices?: { usedInLast8Weeks?: boolean; count?: number; score?: number };
};

type HumanScoreResponse = {
  humanScore?: number;
  score?: number;
  breakdown?: HumanScoreBreakdown;
};

type SharePayload = {
  score: number | null;
  label: string;
  breakdown: HumanScoreBreakdown | null;
};

const SCORE_LEGEND = [
  { label: "Excelente", min: 80 },
  { label: "En forma", min: 60 },
  { label: "En progreso", min: 40 },
  { label: "Arranque", min: 0 },
] as const;

export default function StatisticsPage() {
  const [humanScore, setHumanScore] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<HumanScoreBreakdown | null>(null);
  const [loadingScore, setLoadingScore] = useState(true);
  const [errorScore, setErrorScore] = useState<string | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareInfo, setShareInfo] = useState<string | null>(null);

  useEffect(() => {
    void loadScore();
  }, []);

  const scoreLabel = useMemo(() => getScoreLabel(humanScore), [humanScore]);
  const scoreColor = useMemo(() => getScoreColor(humanScore), [humanScore]);
  const scoreAngle = useMemo(() => `${clamp(humanScore ?? 0, 0, 100) * 3.6}deg`, [humanScore]);

  const attendanceRatio = useMemo(() => {
    const weeks = breakdown?.attendance?.weeksAttended ?? 0;
    const windowWeeks = breakdown?.attendance?.windowWeeks ?? 52;
    if (windowWeeks <= 0) {
      return 0;
    }
    return clamp((weeks / windowWeeks) * 100, 0, 100);
  }, [breakdown]);

  const frequencyRatio = useMemo(() => {
    const avg = breakdown?.frequency?.avgPerWeek ?? 0;
    return clamp((avg / 2) * 100, 0, 100);
  }, [breakdown]);

  const extrasRatio = useMemo(() => {
    const count = breakdown?.extraServices?.count ?? 0;
    return count > 0 ? 100 : 0;
  }, [breakdown]);

  async function loadScore() {
    setLoadingScore(true);
    setErrorScore(null);
    try {
      const { user } = await restoreAuthFromStorage();
      if (!user?.id) {
        throw new Error("No se pudo resolver el usuario");
      }
      const response = await api.get<HumanScoreResponse>(`/api/customers/human-score/${user.id}`);
      const score = Number(response.data?.humanScore ?? response.data?.score);
      if (!Number.isFinite(score)) {
        throw new Error("Score invalido");
      }
      setHumanScore(roundToOne(score));
      setBreakdown(response.data?.breakdown ?? null);
    } catch (error) {
      console.warn("Error loading Fidelis Score", error);
      setErrorScore("No hemos podido cargar tu Fidelis Score.");
      setHumanScore(null);
      setBreakdown(null);
    } finally {
      setLoadingScore(false);
    }
  }

  async function handleRecalculate() {
    setIsRecalculating(true);
    setErrorScore(null);
    try {
      const response = await api.post<HumanScoreResponse>("/api/customers/human-score/recalculate");
      const score = Number(response.data?.humanScore ?? response.data?.score);
      if (!Number.isFinite(score)) {
        throw new Error("Score invalido");
      }
      setHumanScore(roundToOne(score));
      setBreakdown(response.data?.breakdown ?? null);
    } catch (error) {
      console.error("Error recalculating Fidelis Score", error);
      setErrorScore("No hemos podido recalcular tu score ahora mismo.");
    } finally {
      setIsRecalculating(false);
    }
  }

  async function handleShare() {
    if (typeof window === "undefined") {
      return;
    }

    setIsSharing(true);
    setShareInfo(null);

    const shareMessage =
      humanScore !== null
        ? `Mi Fidelis Score actual es ${humanScore.toFixed(1)} sobre 100.`
        : "Mi Fidelis Score aun no esta disponible.";

    try {
      const imageBlob = await buildShareCard({
        score: humanScore,
        label: scoreLabel,
        breakdown,
      });

      const isNative = Capacitor.getPlatform() !== "web";
      if (isNative) {
        try {
          const nativeUri = await persistImageForNativeShare(imageBlob);
          await Share.share({
            title: "Mi Fidelis Score",
            text: shareMessage,
            url: nativeUri,
            dialogTitle: "Compartir Fidelis Score",
          });
          setShareInfo("Listo, comparte tu Fidelis Score donde quieras.");
          return;
        } catch (nativeError) {
          console.warn("Native share fallback", nativeError);
        }
      }

      const file = new File([imageBlob], "fidelis-score.png", { type: "image/png" });
      const navigatorWithCanShare = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
      };

      if (navigator.share && navigatorWithCanShare.canShare?.({ files: [file] })) {
        await navigator.share({
          title: "Mi Fidelis Score",
          text: shareMessage,
          files: [file],
        });
        setShareInfo("Listo, compartir completado.");
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: "Mi Fidelis Score",
          text: shareMessage,
          url: window.location.href,
        });
        setShareInfo("Listo, compartir completado.");
        return;
      }

      triggerImageDownload(imageBlob, `fidelis-score-${Date.now()}.png`);
      setShareInfo("Hemos descargado la imagen para que la compartas desde tu galeria.");
    } catch (error) {
      console.error("Error preparing score share", error);
      setShareInfo("No hemos podido compartir ahora mismo.");
    } finally {
      setIsSharing(false);
    }
  }

  const ringStyle = {
    "--score-color": scoreColor,
    "--score-angle": scoreAngle,
  } as React.CSSProperties;

  return (
    <div className={styles.page}>
      <div className={styles.heroGlow} />

      <main className={styles.content}>
        <section className={styles.heroCard}>
          <div className={styles.heroHead}>
            <span className={styles.chip}>
              <Sparkles size={14} />
              Fidelis Score
            </span>
            <h1 className={styles.title}>Tu rendimiento de entrenamiento</h1>
            <p className={styles.subtitle}>
              Cuanto mas regular eres, mayor impacto tiene tu score.
            </p>
          </div>

          {loadingScore ? (
            <div className={styles.loadingBlock}>Cargando score...</div>
          ) : (
            <div className={styles.scoreWrap}>
              <div className={styles.scoreRing} style={ringStyle}>
                <div className={styles.scoreInner}>
                  <span className={styles.scoreValue}>
                    {humanScore !== null ? humanScore.toFixed(1) : "--"}
                  </span>
                  <span className={styles.scoreScale}>/100</span>
                </div>
              </div>
              <p className={styles.scoreLabel}>{scoreLabel}</p>
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleShare}
              disabled={isSharing || loadingScore}
            >
              <Share2 size={16} />
              {isSharing ? "Preparando..." : "Compartir"}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleRecalculate}
              disabled={isRecalculating || loadingScore}
            >
              <RefreshCw size={16} className={isRecalculating ? styles.spin : ""} />
              {isRecalculating ? "Recalculando..." : "Actualizar"}
            </button>
          </div>

          {shareInfo && <p className={styles.notice}>{shareInfo}</p>}
          {errorScore && <p className={styles.error}>{errorScore}</p>}
        </section>

        <section className={styles.metricsGrid}>
          <article className={styles.metricCard}>
            <div className={styles.metricTop}>
              <Activity size={18} />
              <span className={styles.metricTitle}>Asistencia anual</span>
            </div>
            <p className={styles.metricValue}>
              {formatNumber(breakdown?.attendance?.weeksAttended, 0)} /{" "}
              {breakdown?.attendance?.windowWeeks ?? 52} semanas
            </p>
            <p className={styles.metricHint}>
              +{formatNumber(breakdown?.attendance?.score, 1)} pts
            </p>
            <ProgressBar percentage={attendanceRatio} />
          </article>

          <article className={styles.metricCard}>
            <div className={styles.metricTop}>
              <CalendarClock size={18} />
              <span className={styles.metricTitle}>Frecuencia semanal</span>
            </div>
            <p className={styles.metricValue}>
              {formatNumber(breakdown?.frequency?.avgPerWeek, 1)} entrenos/sem
            </p>
            <p className={styles.metricHint}>
              {formatNumber(breakdown?.frequency?.trainingsLast12Weeks, 0)} entrenos en 12 semanas
            </p>
            <p className={styles.metricHint}>
              +{formatNumber(breakdown?.frequency?.score, 1)} pts
            </p>
            <ProgressBar percentage={frequencyRatio} />
          </article>

          <article className={styles.metricCard}>
            <div className={styles.metricTop}>
              <TrendingUp size={18} />
              <span className={styles.metricTitle}>Servicios extra</span>
            </div>
            <p className={styles.metricValue}>
              {formatNumber(breakdown?.extraServices?.count, 0)} sesiones en 8 semanas
            </p>
            <p className={styles.metricHint}>
              {breakdown?.extraServices?.usedInLast8Weeks ? "Has activado bonus" : "Sin bonus aun"}
            </p>
            <p className={styles.metricHint}>
              +{formatNumber(breakdown?.extraServices?.score, 1)} pts
            </p>
            <ProgressBar percentage={extrasRatio} />
          </article>
        </section>

        <section className={styles.detailCard}>
          <h2 className={styles.detailTitle}>Como interpretar el score</h2>
          <p className={styles.detailSubtitle}>
            Fidelis Score resume constancia, frecuencia y uso de servicios complementarios.
          </p>

          <div className={styles.legend}>
            {SCORE_LEGEND.map((item) => (
              <div className={styles.legendItem} key={item.label}>
                <span
                  className={styles.legendDot}
                  style={{ backgroundColor: getScoreColor(item.min) }}
                />
                <span>{item.label}</span>
                <span className={styles.legendThreshold}>{item.min}+</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className={styles.progressTrack}>
      <div className={styles.progressFill} style={{ width: `${Math.round(percentage)}%` }} />
    </div>
  );
}

function getScoreLabel(score: number | null): string {
  if (score === null) return "Sin datos";
  if (score >= 80) return "Excelente ritmo";
  if (score >= 60) return "Muy buen progreso";
  if (score >= 40) return "En progreso";
  return "Arranque activo";
}

function getScoreColor(score: number | null): string {
  if (score === null) return "#64748b";
  if (score >= 80) return "#0f766e";
  if (score >= 60) return "#0284c7";
  if (score >= 40) return "#f59e0b";
  return "#ef4444";
}

function formatNumber(value: number | undefined, decimals: number): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0";
  }
  return value.toFixed(decimals);
}

function roundToOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

async function buildShareCard({ score, label, breakdown }: SharePayload): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas no disponible");
  }

  const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, "#e2e8f0");
  gradient.addColorStop(0.5, "#dbeafe");
  gradient.addColorStop(1, "#ccfbf1");
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = "rgba(15, 23, 42, 0.08)";
  context.beginPath();
  context.arc(180, 140, 220, 0, Math.PI * 2);
  context.fill();
  context.beginPath();
  context.arc(920, 1120, 260, 0, Math.PI * 2);
  context.fill();

  roundRect(context, 80, 100, 920, 1140, 42, "rgba(255,255,255,0.93)");

  const centerX = 540;
  context.textAlign = "center";
  context.fillStyle = "#0f172a";
  context.font = "900 66px Poppins, Segoe UI, sans-serif";
  context.fillText("FIDELIS SCORE", centerX, 220);
  context.fillStyle = "#334155";
  context.font = "500 34px Poppins, Segoe UI, sans-serif";
  context.fillText("Comparte tu evolucion", centerX, 272);

  const ringRadius = 150;
  const ringThickness = 28;
  const scoreValue = clamp(score ?? 0, 0, 100);
  const scoreFraction = scoreValue / 100;
  const ringColor = getScoreColor(score);

  context.lineWidth = ringThickness;
  context.strokeStyle = "rgba(148, 163, 184, 0.35)";
  context.beginPath();
  context.arc(centerX, 470, ringRadius, 0, Math.PI * 2);
  context.stroke();

  context.strokeStyle = ringColor;
  context.beginPath();
  context.arc(centerX, 470, ringRadius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * scoreFraction);
  context.stroke();

  context.fillStyle = "#ffffff";
  context.beginPath();
  context.arc(centerX, 470, ringRadius - ringThickness, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = "#0f172a";
  context.font = "800 72px Poppins, Segoe UI, sans-serif";
  context.fillText(score !== null ? score.toFixed(1) : "--", centerX, 492);
  context.fillStyle = "#475569";
  context.font = "600 30px Poppins, Segoe UI, sans-serif";
  context.fillText(`${label} - sobre 100`, centerX, 560);

  const lines = [
    `Asistencia anual: ${formatNumber(breakdown?.attendance?.weeksAttended, 0)} / ${
      breakdown?.attendance?.windowWeeks ?? 52
    } semanas`,
    `Frecuencia: ${formatNumber(breakdown?.frequency?.avgPerWeek, 1)} entrenos por semana`,
    `Servicios extra: ${formatNumber(breakdown?.extraServices?.count, 0)} sesiones en 8 semanas`,
  ];

  context.textAlign = "left";
  context.font = "600 32px Poppins, Segoe UI, sans-serif";
  lines.forEach((line, index) => {
    const y = 740 + index * 94;
    context.fillStyle = "#1e293b";
    context.fillText(line, 140, y);
  });

  context.textAlign = "center";
  context.fillStyle = "#0f766e";
  context.font = "700 32px Poppins, Segoe UI, sans-serif";
  context.fillText("Generado desde la app Fidelis", centerX, 1180);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("No se pudo generar la imagen"));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
}

async function persistImageForNativeShare(blob: Blob): Promise<string> {
  const base64DataUrl = await blobToBase64(blob);
  const payload = base64DataUrl.includes(",") ? base64DataUrl.split(",")[1] : base64DataUrl;
  const filename = `fidelis-score-${Date.now()}.png`;

  await Filesystem.writeFile({
    path: filename,
    data: payload,
    directory: Directory.Cache,
  });

  const fileUri = await Filesystem.getUri({
    path: filename,
    directory: Directory.Cache,
  });
  return fileUri.uri;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Formato de imagen invalido"));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(new Error("No se pudo leer la imagen"));
    reader.readAsDataURL(blob);
  });
}

function triggerImageDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: string
) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
  context.fillStyle = fill;
  context.fill();
}
